import { useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import type { ProbationTarget } from '@/utils/approval/probationEvaluation';
import { ProbationEvaluationRoot } from './styles';

type TableProps = {
  target: ProbationTarget;
  t: TFunction;
};

/** 레거시 #employee_text_area 안쪽 마크업(class 동일) */
const ProbationTargetTable = ({ target, t }: TableProps) => (
  <>
    <label className="mb-5">{t('수습평가 대상자 정보')}</label>
    <table className="table table-orders table-bordered table-striped table-responsive no-margin">
      <tbody>
        <tr>
          <th className="text-center">{t('이름')}</th>
          <th className="text-center">{t('직급')}</th>
          <th className="text-center">{t('소속')}</th>
          <th className="text-center">{t('수습기간')}</th>
        </tr>
        <tr>
          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
            {target.name}
          </td>
          <td className="text-center">{target.positionName}</td>
          <td className="text-center">{target.organizationName}</td>
          <td className="text-center">{target.period}</td>
        </tr>
      </tbody>
    </table>
  </>
);

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildProbationTargetContentHtml = (
  target: ProbationTarget,
  t: TFunction,
): string =>
  renderToStaticMarkup(<ProbationTargetTable target={target} t={t} />);

// 레거시 content 끝의 <br/><br/> 는 여백용이라 묶음 간격 규칙과 겹침
const DROP_TAGS = new Set(['style', 'script', 'link', 'br']);
const TABLE_PARTS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr']);
const isBlankText = (node: ReactNode) =>
  typeof node === 'string' && !node.trim();

/** 저장 content(레거시 문서 포함) 그대로 — 표·제목만 공통 양식 컴포넌트로 */
export const ProbationTargetSnapshot = ({ content }: { content: string }) => {
  const transform = useCallback<ElementTransform>(
    ({ tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (TABLE_PARTS.has(tagName)) {
        const kept = children.filter((c) => !isBlankText(c));
        children.splice(0, children.length, ...kept);
      }
      const key = props.key as string;
      if (tagName === 'label') {
        return (
          <InfoSectionTitle key={key} sx={{ mb: 1 }}>
            {children}
          </InfoSectionTitle>
        );
      }
      if (tagName === 'table') {
        return <ApprovalFormTable key={key}>{children}</ApprovalFormTable>;
      }
      return undefined;
    },
    [],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <ProbationEvaluationRoot>{rendered}</ProbationEvaluationRoot>;
};
